import { z } from 'zod';
import { PRODUCT_GRANT_TYPES, SELLABLE_CONTENT_KINDS } from '../domain/payments.js';
import { SUBSCRIPTION_PLANS } from '../domain/subscription.js';

/**
 * Команды администратора по заказам и товарам (docs/PAYMENTS.md, этап 2).
 *
 * Каждое действие с заказом требует причины: она остаётся в журнале заказа
 * рядом с тем, кто и когда это сделал.
 */

const reason = z.string().trim().min(3, 'Опишите причину').max(500);

/** Разбор: выдать по заказу выбранный товар выбранному пользователю. */
export const orderGrantSchema = z.object({
  productId: z.string().min(1),
  userId: z.string().min(1),
  note: reason,
  /**
   * Если Robokassa оплату не подтвердила (не ответила, расхождение),
   * администратор подтверждает её сам, сверив операцию в кабинете Robokassa.
   */
  paymentConfirmed: z.boolean().optional(),
});
export type OrderGrantInput = z.infer<typeof orderGrantSchema>;

/** Привязать заказ к пользователю (например, в почте опечатка) и выдать по нему. */
export const orderLinkSchema = z.object({
  userId: z.string().min(1),
  note: reason,
});
export type OrderLinkInput = z.infer<typeof orderLinkSchema>;

/** Отметить возврат — после того как деньги возвращены в кабинете Robokassa. */
export const orderRefundSchema = z.object({
  note: reason,
  refundConfirmed: z.literal(true, {
    errorMap: () => ({ message: 'Подтвердите, что деньги возвращены в Robokassa' }),
  }),
});
export type OrderRefundInput = z.infer<typeof orderRefundSchema>;

export const orderNoteSchema = z.object({ note: reason });
export type OrderNoteInput = z.infer<typeof orderNoteSchema>;

/**
 * Заказ, уведомление о котором не пришло.
 *
 * Оплату подтверждает Robokassa по API, если проверка настроена, а иначе —
 * администратор: что он сверил в кабинете Robokassa (номер счёта, сумма, дата)
 * — обязательный текст, он уходит в журнал заказа.
 */
export const manualOrderSchema = z.object({
  invoiceId: z.string().trim().regex(/^\d{1,20}$/, 'Номер счёта Robokassa — только цифры'),
  email: z.string().trim().toLowerCase().email('Укажите почту покупателя'),
  productId: z.string().min(1),
  amountRub: z.number().positive().max(1_000_000),
  paidAt: z.string().datetime(),
  attestation: z
    .object({
      checkedInRobokassa: z.literal(true),
      comment: z.string().trim().min(10, 'Опишите, что сверено в кабинете Robokassa').max(500),
    })
    .nullable(),
});
export type ManualOrderInput = z.infer<typeof manualOrderSchema>;

/** Сверка: номера успешных операций из кабинета Robokassa, по одному в строке или через запятую. */
export const reconcileSchema = z.object({
  invoiceIds: z.string().max(20_000),
});
export type ReconcileInput = z.infer<typeof reconcileSchema>;

const productCode = z
  .string()
  .trim()
  .regex(/^[A-Z0-9_]{3,40}$/, 'Код — латинские заглавные буквы, цифры и «_»');

/** «Что входит» — текст на карточке покупки. Пусто — не показывается. */
const productDescription = z
  .string()
  .trim()
  .max(1000)
  .nullable()
  .optional()
  .transform((value) => (value ? value : null));

export const createProductSchema = z
  .object({
    /** Не передан — сервер придумает сам (`LIGHT_30D_2`, `TRAINING_1`…). */
    code: productCode.optional(),
    title: z.string().trim().min(2).max(120),
    description: productDescription,
    grantType: z.enum(PRODUCT_GRANT_TYPES),
    plan: z.enum(SUBSCRIPTION_PLANS).nullable(),
    days: z.number().int().min(1).max(3650).nullable(),
    years: z.number().int().min(1).max(10).nullable().optional(),
    gameId: z.string().min(1).nullable(),
    contentKind: z.enum(SELLABLE_CONTENT_KINDS).nullable().optional(),
    contentId: z.string().min(1).nullable().optional(),
    priceRub: z.number().positive().max(1_000_000),
  })
  .refine(
    (value) =>
      value.grantType === 'plan'
        ? value.plan !== null && ((value.days !== null) !== (value.years != null))
        : value.grantType === 'game_ownership'
          ? value.gameId !== null
          : Boolean(value.contentKind && value.contentId),
    'Для тарифа укажите тариф и срок, для игры — игру, для материала — материал',
  );
export type CreateProductInput = z.input<typeof createProductSchema>;

export const updateProductSchema = z.object({
  title: z.string().trim().min(2).max(120),
  /** Не передано — описание не меняется. */
  description: productDescription,
  active: z.boolean(),
  days: z.number().int().min(1).max(3650).nullable(),
  years: z.number().int().min(1).max(10).nullable().optional(),
});
export type UpdateProductInput = z.input<typeof updateProductSchema>;

/** Новая цена. Прежняя принимается ещё `graceDays` дней — пока Tilda не обновлена у всех. */
export const changePriceSchema = z.object({
  priceRub: z.number().positive().max(1_000_000),
  graceDays: z.number().int().min(0).max(60),
});
export type ChangePriceInput = z.infer<typeof changePriceSchema>;

/**
 * Адреса страницы тарифов на Tilda для кнопок «Оформить», «Продлить»,
 * «Перейти на Про» (docs/PAYMENTS.md, §10).
 *
 * Их два: рабочий — страница настоящих продаж, тестовый — страница тестового
 * оформления с тестовой оплатой Robokassa. Какой из них действует, решает
 * режим приёма оплат; рабочий адрес в тестовом режиме не подставляется.
 */
const tariffPageUrl = z
  .string()
  .trim()
  .url()
  .refine((value) => value.startsWith('https://'), 'Адрес должен начинаться с https://')
  .nullable();

/**
 * Как оформляется покупка: `tilda` — кнопки ведут на страницу тарифов Tilda
 * (прежняя схема, остаётся до приёмки прямой оплаты); `direct` — счёт
 * создаётся в Creativitas и оплачивается в Robokassa напрямую
 * (docs/payments/ROBOKASSA_DIRECT.md).
 */
export const CHECKOUT_MODES = ['tilda', 'direct'] as const;
export type CheckoutMode = (typeof CHECKOUT_MODES)[number];

export const offerSettingsSchema = z.object({
  url: tariffPageUrl,
  testUrl: tariffPageUrl.default(null),
  /** Не передан — остаётся прежний. */
  checkout: z.enum(CHECKOUT_MODES).optional(),
});
export type OfferSettingsInput = z.infer<typeof offerSettingsSchema>;
export type OfferSettings = { url: string | null; testUrl: string | null; checkout: CheckoutMode };

/**
 * Режим приёма оплат — переключатель в «Продажи → Тарифы и оплата». В тестовом
 * заказы записываются, но прав не выдают; боевой включается, только когда на
 * сервере есть всё нужное для настоящих продаж.
 */
export const PAYMENT_MODES = ['test', 'live'] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];
export const paymentModeSchema = z.object({ mode: z.enum(PAYMENT_MODES) });
export type PaymentModeInput = z.infer<typeof paymentModeSchema>;

/**
 * Оформление прямой оплаты. Покупатель называет только товар: цена, срок и
 * режим — с сервера. Ключ нажатия защищает от двойного щелчка.
 */
export const checkoutSchema = z.object({
  productCode: z.string().trim().regex(/^[A-Z0-9_]{3,40}$/),
  idempotencyKey: z.string().trim().regex(/^[A-Za-z0-9_-]{8,64}$/),
  /** Покупатель отметил согласие на автопродление (docs/payments/RECURRING.md). */
  recurring: z.boolean().optional(),
});
export type CheckoutInput = z.infer<typeof checkoutSchema>;

/** Автопродление доступно покупателям — включает администратор, когда Robokassa подключила услугу. */
export const recurringSettingsSchema = z.object({ enabled: z.boolean() });
export type RecurringSettingsInput = z.infer<typeof recurringSettingsSchema>;

/** Отключение автопродления администратором — по обращению покупателя, с причиной. */
export const adminRecurringCancelSchema = z.object({ note: z.string().trim().min(3).max(500) });
export type AdminRecurringCancelInput = z.infer<typeof adminRecurringCancelSchema>;
